import React, { useState, useEffect, useMemo } from 'react';
import PinGate from './PinGate';
import { auth, db } from '../firebase';
import {
  collection, onSnapshot, doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc,
  serverTimestamp
} from 'firebase/firestore';

// Phil-only hosting billing page. Replaces "CMG Website Hosting.xlsx".
// Data is completely independent of hub client data by design.
// Shares the 4-digit PIN with /residuals (residualsMeta/settings).

const SYNC_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/syncNexcessSites';
const UNLOCK_KEY = 'hosting_unlocked';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

async function sha256(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

const money = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(Math.round(n)).toLocaleString();
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const normDomain = (d) => (d || '').toLowerCase().trim().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
const yearTotal = (site) => (site.months || []).reduce((s, v) => s + num(v), 0);

// Describe a site's billing pattern from its 12-month array.
function billingLabel(site) {
  const m = (site.months || []).map(num);
  const nz = m.map((v, i) => [i, v]).filter(([, v]) => v > 0);
  if (nz.length === 0) return site.status === 'suspended' ? 'suspended' : 'free';
  if (nz.length === 12 && nz.every(([, v]) => v === nz[0][1])) return `Monthly ${money(nz[0][1])}`;
  return nz.map(([i, v]) => `${MONTHS[i]} ${money(v)}`).join(' · ');
}

const STATUS_STYLE = {
  active:    { color: 'var(--accent-green)', label: 'active' },
  free:      { color: 'var(--text-muted)', label: 'free' },
  suspended: { color: 'var(--accent-red)', label: 'suspended' },
};

// ---------- Site editor ----------
function SiteEditor({ initial, onSave, onCancel }) {
  const init = initial || {};
  const initMonths = (init.months || Array(12).fill(0)).map(num);
  const nz = initMonths.map((v, i) => [i, v]).filter(([, v]) => v > 0);
  const initType = nz.length === 0 ? 'free'
    : (nz.length === 12 && nz.every(([, v]) => v === nz[0][1])) ? 'monthly'
    : nz.length === 1 ? 'annual' : 'custom';

  const [site, setSite] = useState({
    domain: init.domain || '', contactName: init.contactName || '', email: init.email || '',
    registrar: init.registrar || '', registrarUser: init.registrarUser || '',
    notes: init.notes || '', status: init.status || 'active',
    autoBilling: !!init.autoBilling,
  });
  const [type, setType] = useState(initType);
  const [amount, setAmount] = useState(nz.length ? nz[0][1] : 299);
  const [month, setMonth] = useState(nz.length === 1 ? nz[0][0] : new Date().getMonth());
  const [custom, setCustom] = useState(initMonths);

  const set = (k, v) => setSite(s => ({ ...s, [k]: v }));
  const input = (k, placeholder, width = 180) => (
    <input value={site[k]} placeholder={placeholder} onChange={e => set(k, e.target.value)}
      style={{ width, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
  );

  const buildMonths = () => {
    if (type === 'free') return Array(12).fill(0);
    if (type === 'monthly') return Array(12).fill(num(amount));
    if (type === 'annual') { const m = Array(12).fill(0); m[month] = num(amount); return m; }
    return custom.map(num);
  };

  const save = () => onSave({
    ...site,
    domain: normDomain(site.domain),
    months: buildMonths(),
    status: type === 'free' && site.status === 'active' ? 'free' : site.status,
  });

  const selStyle = { padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, background: 'var(--bg-tertiary)', borderRadius: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Domain<br />{input('domain', 'example.com', 220)}</label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Contact<br />{input('contactName', 'First name', 120)}</label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Invoice email<br />{input('email', 'name@example.com', 220)}</label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Registrar<br />{input('registrar', 'GoDaddy', 120)}</label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Registrar user<br />{input('registrarUser', 'username / acct #', 150)}</label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Status<br />
          <select value={site.status} onChange={e => set('status', e.target.value)} style={selStyle}>
            <option value="active">active</option><option value="free">free</option><option value="suspended">suspended</option>
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Billing<br />
          <select value={type} onChange={e => setType(e.target.value)} style={selStyle}>
            <option value="annual">Annual (one invoice/yr)</option>
            <option value="monthly">Monthly (every month)</option>
            <option value="custom">Custom (per month)</option>
            <option value="free">Free (no billing)</option>
          </select>
        </label>
        {(type === 'annual' || type === 'monthly') && (
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Amount<br />
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              style={{ width: 90, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
          </label>
        )}
        {type === 'annual' && (
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Renewal month<br />
            <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} style={selStyle}>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </label>
        )}
        <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
          <input type="checkbox" checked={site.autoBilling} onChange={e => set('autoBilling', e.target.checked)} style={{ width: 15, height: 15 }} />
          Monthly Auto Billing (Stripe — no manual invoice)
        </label>
      </div>
      {type === 'custom' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {MONTHS.map((m, i) => (
            <label key={m} style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{m}<br />
              <input type="number" value={custom[i]} onChange={e => setCustom(c => c.map((v, j) => j === i ? e.target.value : v))}
                style={{ width: 64, padding: '4px 6px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
            </label>
          ))}
        </div>
      )}
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Notes<br />
        <input value={site.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything worth remembering"
          style={{ width: '100%', maxWidth: 640, padding: '5px 7px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-sm btn-success" disabled={!normDomain(site.domain)} onClick={save}>Save</button>
        <button className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ---------- Main ----------
export default function Hosting() {
  const [gate, setGate] = useState('loading');
  const [gateError, setGateError] = useState('');
  const [pinHash, setPinHash] = useState(null);

  const [sites, setSites] = useState([]);
  const [nexcess, setNexcess] = useState(null);
  const [settings, setSettings] = useState({});
  const [invoiced, setInvoiced] = useState({ done: [] });
  const [ym, setYm] = useState(() => new Date().toLocaleDateString('sv-SE').slice(0, 7));
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(null); // null | {prefill}
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [showFlags, setShowFlags] = useState(true);

  const monthIdx = parseInt(ym.slice(5), 10) - 1;

  // Gate — shares the Residuals PIN (residualsMeta/settings).
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'residualsMeta', 'settings'));
        const hash = snap.exists() ? snap.data().pinHash : null;
        setPinHash(hash);
        if (sessionStorage.getItem(UNLOCK_KEY) === '1' && hash) setGate('open');
        else setGate(hash ? 'locked' : 'setup');
      } catch (err) {
        setGate('denied');
      }
    })();
  }, []);

  useEffect(() => {
    if (gate !== 'open') return;
    const unsubs = [
      onSnapshot(collection(db, 'hostingSites'), s => setSites(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(doc(db, 'hostingMeta', 'nexcess'), s => setNexcess(s.exists() ? s.data() : null)),
      onSnapshot(doc(db, 'hostingMeta', 'settings'), s => setSettings(s.exists() ? s.data() : {})),
    ];
    return () => unsubs.forEach(u => u());
  }, [gate]);

  useEffect(() => {
    if (gate !== 'open') return;
    return onSnapshot(doc(db, 'hostingMeta', `invoiced-${ym}`), s => setInvoiced(s.exists() ? s.data() : { done: [] }));
  }, [gate, ym]);

  const handlePin = async (pin) => {
    setGateError('');
    const hash = await sha256(pin);
    if (gate === 'setup') {
      await setDoc(doc(db, 'residualsMeta', 'settings'), { pinHash: hash, updatedAt: serverTimestamp() }, { merge: true });
      setPinHash(hash);
      sessionStorage.setItem(UNLOCK_KEY, '1');
      setGate('open');
    } else if (hash === pinHash) {
      sessionStorage.setItem(UNLOCK_KEY, '1');
      setGate('open');
    } else {
      setGateError('Wrong PIN.');
    }
  };
  const lock = () => { sessionStorage.removeItem(UNLOCK_KEY); setGate('locked'); };

  // ----- computed -----
  const active = useMemo(() => sites.filter(s => s.status === 'active'), [sites]);
  const totals = useMemo(() => {
    const yearly = active.reduce((s, x) => s + yearTotal(x), 0);
    const due = active.filter(s => num((s.months || [])[monthIdx]) > 0);
    const dueTotal = due.reduce((s, x) => s + num(x.months[monthIdx]), 0);
    return { yearly, avgMonthly: yearly / 12, due, dueTotal };
  }, [active, monthIdx]);

  const dismissedLive = settings.dismissedLive || [];
  const dismissedBilled = settings.dismissedBilled || [];

  const flags = useMemo(() => {
    if (!nexcess || !nexcess.sites) return null;
    const live = new Set(nexcess.sites.map(s => normDomain(s.domain)));
    const billed = new Set(sites.map(s => normDomain(s.domain)));
    const liveNotBilled = [...live].filter(d => !billed.has(d) && !dismissedLive.includes(d)).sort();
    const billedNotLive = [...billed].filter(d => !live.has(d) && !dismissedBilled.includes(d)).sort();
    return { liveNotBilled, billedNotLive, liveCount: live.size };
  }, [nexcess, sites, dismissedLive, dismissedBilled]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = q
      ? sites.filter(s => [s.domain, s.contactName, s.email, s.registrar, s.notes].join(' ').toLowerCase().includes(q))
      : sites;
    return [...list].sort((a, b) => (a.domain || '').localeCompare(b.domain || ''));
  }, [sites, search]);

  // ----- actions -----
  const saveSite = async (id, data) => {
    if (id) await updateDoc(doc(db, 'hostingSites', id), { ...data, updatedAt: serverTimestamp() });
    else await addDoc(collection(db, 'hostingSites'), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    setEditingId(null); setAdding(null);
  };
  const removeSite = async (s) => {
    if (window.confirm(`Delete ${s.domain}?`)) await deleteDoc(doc(db, 'hostingSites', s.id));
  };
  const toggleInvoiced = async (domain) => {
    const done = invoiced.done || [];
    const next = done.includes(domain) ? done.filter(d => d !== domain) : [...done, domain];
    await setDoc(doc(db, 'hostingMeta', `invoiced-${ym}`), { done: next, updatedAt: serverTimestamp() }, { merge: true });
  };
  const settingsRef = doc(db, 'hostingMeta', 'settings');
  const dismissLive = (d) => setDoc(settingsRef, { dismissedLive: [...dismissedLive, d] }, { merge: true });
  const dismissBilled = (d) => setDoc(settingsRef, { dismissedBilled: [...dismissedBilled, d] }, { merge: true });
  const restoreLive = (d) => updateDoc(settingsRef, { dismissedLive: dismissedLive.filter(x => x !== d) });
  const restoreBilled = (d) => updateDoc(settingsRef, { dismissedBilled: dismissedBilled.filter(x => x !== d) });

  const syncNow = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(SYNC_URL, { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
      const data = await res.json();
      setSyncMsg(res.ok ? `Synced — ${data.named} live sites found.` : `Sync failed: ${data.error}`);
    } catch (err) {
      setSyncMsg('Sync failed: ' + err.message);
    }
    setSyncing(false);
  };

  // ----- gates -----
  if (gate === 'loading') return <div className="loading-screen"><div className="spinner-lg"></div></div>;
  if (gate === 'denied') return (
    <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 40 }}>⛔</div>
      <p>This page is restricted.</p>
    </div>
  );
  if (gate !== 'open') return <PinGate mode={gate} title="Hosting" onSubmit={handlePin} error={gateError} />;

  const doneSet = new Set(invoiced.done || []);
  const manualDue = totals.due.filter(s => !s.autoBilling);
  const autoDue = totals.due.filter(s => s.autoBilling);
  const dueDone = manualDue.filter(s => doneSet.has(s.domain));
  const autoDueTotal = autoDue.reduce((s, x) => s + num(x.months[monthIdx]), 0);
  const manualDueTotal = manualDue.reduce((s, x) => s + num(x.months[monthIdx]), 0);
  const shiftMonth = (delta) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const monthName = `${MONTHS[monthIdx]} ${ym.slice(0, 4)}`;

  const summaryCards = [
    { label: 'Hosted Sites (billed)', value: sites.length + (flags ? ` / ${flags.liveCount} live` : '') },
    { label: 'Hosting Revenue / yr', value: money(totals.yearly) },
    { label: 'Average / month', value: money(totals.avgMonthly) },
    { label: `Due in ${MONTHS[monthIdx]}`, value: `${totals.due.length} · ${money(totals.dueTotal)}` },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Hosting</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-secondary" onClick={syncNow} disabled={syncing}>
            {syncing ? 'Syncing…' : '🔄 Sync Nexcess'}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={lock}>🔒 Lock</button>
        </div>
      </div>
      {syncMsg && <div style={{ color: syncMsg.startsWith('Synced') ? 'var(--accent-green)' : 'var(--accent-red)', fontSize: 13, marginBottom: 10 }}>{syncMsg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="card" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-secondary)', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Billing day */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            🧾 Billing Day — {monthName}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-sm btn-secondary" onClick={() => shiftMonth(-1)}>←</button>
            <button className="btn btn-sm btn-secondary" onClick={() => shiftMonth(1)}>→</button>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {dueDone.length} of {manualDue.length} invoiced · {money(dueDone.reduce((s, x) => s + num(x.months[monthIdx]), 0))} of {money(manualDueTotal)}
              {autoDue.length > 0 && <> · {autoDue.length} on auto-pay ({money(autoDueTotal)})</>}
            </span>
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {totals.due.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Nothing due this month.</span>}
          {totals.due.map(s => {
            if (s.autoBilling) return (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7,
                background: 'var(--bg-tertiary)', opacity: 0.5,
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: 'var(--text-muted)', color: 'var(--bg-primary)' }}>AUTO</span>
                <span style={{ minWidth: 232, fontWeight: 600 }}>{s.domain}</span>
                <span style={{ width: 70, textAlign: 'right', fontWeight: 700 }}>{money(num(s.months[monthIdx]))}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{s.contactName}</span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Stripe auto-billing — nothing to do</span>
                {s.notes && <span title={s.notes} style={{ fontSize: 13 }}>📝</span>}
              </div>
            );
            const done = doneSet.has(s.domain);
            return (
              <label key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7,
                background: done ? 'rgba(46, 158, 91, 0.10)' : 'var(--bg-tertiary)', cursor: 'pointer',
              }}>
                <input type="checkbox" checked={done} onChange={() => toggleInvoiced(s.domain)} style={{ width: 16, height: 16 }} />
                <span style={{ minWidth: 260, fontWeight: 600, textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--text-secondary)' : 'var(--text-primary)' }}>{s.domain}</span>
                <span style={{ width: 70, textAlign: 'right', fontWeight: 700 }}>{money(num(s.months[monthIdx]))}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{s.contactName}</span>
                {s.email && <a href={`mailto:${s.email}`} style={{ fontSize: 13, color: '#ffffff', textDecorationColor: 'var(--text-muted)', textUnderlineOffset: 3 }} onClick={e => e.stopPropagation()}>{s.email}</a>}
                {s.notes && <span title={s.notes} style={{ fontSize: 13 }}>📝</span>}
              </label>
            );
          })}
        </div>
      </div>

      {/* Nexcess sync flags */}
      {flags && (flags.liveNotBilled.length > 0 || flags.billedNotLive.length > 0 || dismissedLive.length > 0 || dismissedBilled.length > 0) && (
        <div className="card" style={{ marginBottom: 20, borderColor: (flags.liveNotBilled.length || flags.billedNotLive.length) ? 'var(--accent-orange)' : 'var(--border)' }}>
          <div className="card-title" style={{ cursor: 'pointer' }} onClick={() => setShowFlags(v => !v)}>
            {(flags.liveNotBilled.length || flags.billedNotLive.length)
              ? `⚠️ Nexcess checks (${flags.liveNotBilled.length + flags.billedNotLive.length})`
              : '✓ Nexcess checks clear'} {showFlags ? '▾' : '▸'}
            <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>
              last synced {nexcess.syncedAt ? new Date(nexcess.syncedAt.seconds * 1000).toLocaleString() : '—'}
            </span>
          </div>
          {showFlags && (
            <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {flags.liveNotBilled.map(d => (
                <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span><span style={{ color: 'var(--accent-orange)' }}>●</span> <strong>{d}</strong> is live on Nexcess but has no billing row — unbilled?</span>
                  <button className="btn btn-sm btn-primary" onClick={() => { setAdding({ domain: d }); setEditingId(null); }}>+ Add</button>
                  <button className="btn btn-sm btn-secondary" onClick={() => dismissLive(d)}>Dismiss</button>
                </div>
              ))}
              {flags.billedNotLive.map(d => (
                <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span><span style={{ color: 'var(--accent-blue)' }}>●</span> <strong>{d}</strong> has a billing row but is NOT on Nexcess — gone, renamed, or hosted elsewhere?</span>
                  <button className="btn btn-sm btn-secondary" onClick={() => dismissBilled(d)}>Dismiss</button>
                </div>
              ))}
              {(dismissedLive.length > 0 || dismissedBilled.length > 0) && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {dismissedLive.map(d => (
                    <div key={d}>🙈 {d} (live, intentionally unbilled) <button className="btn-logout" style={{ fontSize: 11 }} onClick={() => restoreLive(d)}>restore</button></div>
                  ))}
                  {dismissedBilled.map(d => (
                    <div key={d}>🙈 {d} (billed, not on server) <button className="btn-logout" style={{ fontSize: 11 }} onClick={() => restoreBilled(d)}>restore</button></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* All sites */}
      <div className="card" style={{ marginBottom: 40, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px 0', flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>All Sites ({sites.length})</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
              style={{ width: 200, padding: '5px 9px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }} />
            {!adding && <button className="btn btn-sm btn-primary" onClick={() => { setAdding({}); setEditingId(null); }}>+ Add site</button>}
          </div>
        </div>
        {adding && (
          <div style={{ padding: '12px 16px' }}>
            <SiteEditor initial={adding} onSave={(d) => saveSite(null, d)} onCancel={() => setAdding(null)} />
          </div>
        )}
        <div style={{ overflow: 'auto', maxHeight: '75vh', marginTop: 10 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                {['Domain', 'Contact', 'Invoice Email', 'Billing', '$/yr', 'Registrar', 'Status', ''].map((label, i) => (
                  <th key={i} style={{ position: 'sticky', top: 0, zIndex: 2, boxShadow: 'inset 0 -1px 0 var(--border)', textAlign: label === '$/yr' ? 'right' : 'left' }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                if (editingId === s.id) return (
                  <tr key={s.id}><td colSpan={8} style={{ padding: 8 }}>
                    <SiteEditor initial={s} onSave={(d) => saveSite(s.id, d)} onCancel={() => setEditingId(null)} />
                  </td></tr>
                );
                const st = STATUS_STYLE[s.status] || STATUS_STYLE.active;
                return (
                  <tr key={s.id} style={{ opacity: s.status === 'active' ? 1 : 0.6 }}>
                    <td style={{ fontWeight: 600 }}>{s.domain}{s.notes && <span title={s.notes}> 📝</span>}</td>
                    <td>{s.contactName}</td>
                    <td>{s.email && <a href={`mailto:${s.email}`} style={{ color: '#ffffff' }}>{s.email}</a>}</td>
                    <td style={{ fontSize: 12 }}>
                      {billingLabel(s)}
                      {s.autoBilling && <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: 'var(--text-muted)', color: 'var(--bg-primary)' }}>AUTO</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{yearTotal(s) > 0 ? money(yearTotal(s)) : '–'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{[s.registrar, s.registrarUser].filter(Boolean).join(' · ')}</td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>{st.label}</span></td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => { setEditingId(s.id); setAdding(null); }}>✏️</button>{' '}
                      <button className="btn btn-sm btn-danger" onClick={() => removeSite(s)}>🗑</button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 30 }}>
                  {sites.length === 0 ? 'No sites yet. Add one, or run the seed import.' : 'No matches.'}
                </td></tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ position: 'sticky', bottom: 0, background: 'var(--bg-tertiary)', boxShadow: 'inset 0 2px 0 var(--border)' }}>TOTAL (active)</td>
                  <td style={{ position: 'sticky', bottom: 0, background: 'var(--bg-tertiary)', boxShadow: 'inset 0 2px 0 var(--border)' }} colSpan={3}></td>
                  <td style={{ position: 'sticky', bottom: 0, background: 'var(--bg-tertiary)', boxShadow: 'inset 0 2px 0 var(--border)', textAlign: 'right' }}>{money(totals.yearly)}</td>
                  <td style={{ position: 'sticky', bottom: 0, background: 'var(--bg-tertiary)', boxShadow: 'inset 0 2px 0 var(--border)' }} colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
